import { type FC, type ReactNode, useMemo } from 'react';
import type { ChatThreadProps } from './ChatThread.types';
import { getClasses } from '@/_utils/classnames';
import styles from './ChatThread.module.css';
import { Avatar, Icon, Text, Flex } from '@/_components';
import { getDateAgo } from '@/_utils/dates';

const PARTICIPANT_LIMIT = 3 as const;

export const ChatThread: FC<ChatThreadProps> = ({
  replyCount = 0,
  onClick,
  children,
  lastReplyDate,
  participantCount,
}) => {
  const showThread: boolean = Boolean(replyCount && replyCount >= 0);
  const hasParticipants: boolean = !participantCount || participantCount === 0;

  const chatThreadClasses: string = getClasses(styles.chatThread, {
    [styles.chatThreadItem__Participants]: hasParticipants,
  });

  const getExtraAvatar: ReactNode = useMemo(() => {
    if (!participantCount || participantCount === 0) return null;

    const exceedCount: number = participantCount - PARTICIPANT_LIMIT;

    if (exceedCount <= PARTICIPANT_LIMIT) return null;

    return (
      <Avatar
        type="user"
        className={styles.chatThread__Separator__extraAvatar}
        name={`+ ${exceedCount}`}
        size={24}
      />
    );
  }, [participantCount]);

  const getAvatars: ReactNode = useMemo(() => {
    if (hasParticipants) return null;

    return (
      <Flex className={styles.chatThread__Separator__avatars} gap={2}>
        {children}
        {getExtraAvatar}
      </Flex>
    );
  }, [children, hasParticipants, getExtraAvatar]);

  if (!showThread) return null;

  const lastReplyDateAgo: string = getDateAgo(lastReplyDate);

  return (
    <Flex
      className={chatThreadClasses}
      data-component="ChatThread"
      onClick={onClick}
    >
      {getAvatars}
      <Text
        className={styles.chatThread__replies}
        type="small"
        fontWeight="medium"
      >
        {replyCount} Replies
      </Text>
      <Text type="tiny" color="secondary">
        Last reply: {lastReplyDateAgo}
      </Text>
      <Icon
        className={styles.chatThread__activate}
        size={24}
        name="arrow-right"
      />
    </Flex>
  );
};
